import { useCallback, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useFormData } from 'brief-form'
import { routes } from 'commons/constants/routes'
import { Request } from 'commons/utils/request/request'
import { Toast } from 'ui/components/Toast'
import { useConfirmation } from 'ui/hooks'
import { LicenseForm } from '~bank-licenses/types'

import { handlerServerError } from '@orbit/errors'

export const useBehavior = ({ form, returnUrl }: { form: LicenseForm; returnUrl?: string }) => {
  const navigate = useNavigate()
  const { config, Field, Form, isDirty } = useFormData<LicenseForm>({
    initialValue: form,
    onBeforeChange: ({ value, errors }) => {
      if (value.active) {
        return {
          value: {
            ...value,
            nullificationDate: null
          },
          errors
        }
      }
      return {
        value,
        errors
      }
    }
  })
  const [isLeaveConfirmationNeeded, setIsLeaveConfirmationNeeded] = useState(true)

  const { mutate: saveHandler, isPending } = useMutation<LicenseForm, Error, LicenseForm>({
    mutationFn: (opts) => {
      const func = opts.id ? Request.put : Request.post
      return func(Request.urls.bank.license, opts)
    },
    onSuccess: (response) => {
      Toast.successSmall(`License ${form.id ? 'updated' : 'created'}`)
      if (returnUrl) {
        navigate({ to: returnUrl })
      } else {
        if (response.id) {
          navigate({ to: '/licenses/$licenseId', params: { licenseId: response.id.toString() } })
        } else {
          navigate({ to: '/licenses/$licenseId', params: { licenseId: form.id.toString() } })
        }
      }
    },
    onError: (error) => handlerServerError(error, { form: config })
  })

  const { mutate: deleteLicense } = useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => Request.delete(Request.urls.bank.license, { id }),
    onSuccess: () => {
      Toast.success(`License ${form.name} was deleted.`)
      if (form.companyId) {
        navigate({
          to: routes.bank.relationships.details,
          params: { id: form.companyId.toString() },
          hash: 'licenses'
        })
      }
    },
    onError: (error) => handlerServerError(error)
  })

  const cancelEdit = useCallback(() => {
    if (returnUrl) {
      navigate({ to: returnUrl })
    } else {
      navigate({ to: '/licenses/$licenseId', params: { licenseId: form.id.toString() } })
    }
  }, [returnUrl, navigate, form.id])

  const { Confirmation, open } = useConfirmation({
    message: (
      <span>
        Are you sure you want to delete License <i>{form.name}</i>?
      </span>
    ),
    onConfirm: () => deleteLicense({ id: form.id }),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const saveLicense = useCallback(async () => {
    setIsLeaveConfirmationNeeded(false)
    saveHandler(config.value)
  }, [config.value, saveHandler])

  return {
    Form,
    Field,
    config,
    isDirty,
    saveLicense,
    Confirmation,
    deleteLicense: open,
    isLeaveConfirmationNeeded,
    isPending,
    cancelEdit
  }
}
